import { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { TokenStorage } from '../auth/TokenStorage';
import { refreshAccessToken } from '../auth/oauth2Client';

/**
 * 401-triggered refresh-and-retry for the web login session (D9).
 *
 * Mirrors getspeakhub-portal's `interceptors/auth.interceptor.ts`: on a 401,
 * refresh once and replay the failed request with the new token. Portal
 * queues concurrent 401s behind a single RxJS BehaviorSubject; this app has
 * no RxJS dependency, so the equivalent here is a single shared in-flight
 * promise (`refreshInFlight`) that every concurrent 401 awaits instead of
 * each triggering its own refresh call.
 *
 * Bearer-token *attachment* itself doesn't need a request interceptor here:
 * ApiService already merges `defaultHeaders` into every request, and
 * AuthService calls `ApiService.setAuthToken()` at every point a token is
 * obtained (login, refresh, app boot) — see application/services/AuthService.ts.
 * This module owns only the part that genuinely requires interceptor
 * semantics: reacting to a failed request.
 */

interface RetryableConfig extends InternalAxiosRequestConfig {
  _retriedAfterRefresh?: boolean;
}

let refreshInFlight: Promise<string> | null = null;

function isTokenEndpoint(url?: string): boolean {
  return !!url && url.includes('oauth2/token');
}

async function refreshOnce(): Promise<string> {
  if (!refreshInFlight) {
    refreshInFlight = doRefresh().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

async function doRefresh(): Promise<string> {
  const refreshToken = TokenStorage.getRefreshToken();
  if (!refreshToken) {
    throw new Error('[Auth] No refresh token available');
  }
  const tokenResponse = await refreshAccessToken(refreshToken);
  TokenStorage.saveTokens(tokenResponse);
  return tokenResponse.access_token;
}

export function attachAuthInterceptors(client: AxiosInstance): void {
  client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const config = error.config as RetryableConfig | undefined;

      const eligibleForRefresh =
        error.response?.status === 401 &&
        !!config &&
        !config._retriedAfterRefresh &&
        !isTokenEndpoint(config.url) &&
        !!TokenStorage.getRefreshToken();

      if (!eligibleForRefresh || !config) {
        return Promise.reject(error);
      }

      config._retriedAfterRefresh = true;

      try {
        const newAccessToken = await refreshOnce();
        config.headers = config.headers ?? {};
        config.headers['Authorization'] = `Bearer ${newAccessToken}`;
        return client.request(config);
      } catch (refreshError) {
        console.error('[Auth] Token refresh failed, clearing session:', refreshError);
        TokenStorage.clearTokens();
        return Promise.reject(error);
      }
    }
  );
}
